import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import RevealText from "../components/RevealText";
import MagneticButton from "../components/MagneticButton";
import MediaImage from "../components/MediaImage";
import MediaVideo from "../components/MediaVideo";

const STUDIO_GRID = [
  { type: "image", publicId: "studio-image-1_cg4zsg", alt: "Sauce Studio logo and camera setup" },
  { type: "video", publicId: "studio-video-4_afulg4", alt: "Studio in motion" },
  { type: "image", publicId: "studio-image-2_snjhjm", alt: "Studio vanity area" },
  { type: "video", publicId: "studio-video-5_rm5c9z", alt: "Studio atmosphere" },
  { type: "image", publicId: "studio-image-5_fnjyg1", alt: "Studio set and lighting" },
  { type: "video", publicId: "studio-video-1_qpdpvh", alt: "Studio walkthrough" },
];

const STUDIO_VIDEO_IDS = STUDIO_GRID.filter((item) => item.type === "video").map((item) => item.publicId);

const PRINCIPLES = [
  { n: "01", t: "Story before everything", d: "Every great brand starts with a story. Cameras, lighting and design come later." },
  { n: "02", t: "Quality over quantity", d: "We'd rather create one unforgettable film than ten forgettable pieces of content." },
  { n: "03", t: "Cinema, not shortcuts", d: "Real cameras. Intentional lighting. Thoughtful frames. Every project deserves more than convenience." },
  { n: "04", t: "Partners, not vendors", d: "We don't just execute ideas—we challenge them, refine them, and build alongside you." },
];

export default function About() {
  return (
    <div data-testid="page-about" className="pt-[calc(var(--site-header-offset)+2rem)] md:pt-40">
      <section className="px-6 md:px-12 pb-16 md:pb-24">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-6">About — Studio</p>
        <h1 className="font-display uppercase text-[16vw] md:text-[12vw] leading-[0.86] tracking-mega">
          <RevealText>A small studio.</RevealText><br/>
          <RevealText delay={0.06}><em className="font-editorial italic">Loud</em> point of view.</RevealText>
        </h1>
      </section>

      <section className="px-6 md:px-12 pb-24" data-testid="about-studio-grid">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6 max-w-7xl mx-auto">
          {STUDIO_GRID.map((item, i) => (
            <motion.div
              key={item.publicId}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
              className="aspect-[4/5] w-full overflow-hidden bg-black/5"
            >
              {item.type === "video" ? (
                <MediaVideo
                  publicId={item.publicId}
                  videoIds={STUDIO_VIDEO_IDS}
                  title={item.alt}
                  aspect="h-full w-full"
                  autoplayOnView
                />
              ) : (
                <MediaImage
                  publicId={item.publicId}
                  alt={item.alt}
                  eager={i < 3}
                  className="w-full h-full object-cover"
                />
              )}
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 md:py-32 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5">
          <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-2">Who we are</p>
        </div>
        <div className="col-span-12 md:col-span-7">
          <p className="font-editorial italic text-3xl md:text-5xl leading-tight">
            Sauce Studios began in 2024 with a simple ambition: to create work we&apos;d be proud to put our name on.
          </p>
          <p className="mt-8 font-body text-lg md:text-xl text-black/70 max-w-2xl">
            We&apos;re a creative studio based in Jodhpur, partnering with brands that value quality, intention, and original thinking over trends and shortcuts.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 md:py-32 bg-[#F42B29] text-white" data-testid="about-principles">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-white/70 mb-6">Principles</p>
        <h2 className="font-display text-5xl md:text-8xl uppercase tracking-mega mb-16 leading-[0.9]"><RevealText>How we think.</RevealText></h2>
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          {PRINCIPLES.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              className="col-span-12 md:col-span-6 border-t border-white/30 pt-6"
            >
              <div className="flex items-baseline gap-4">
                <span className="font-body uppercase text-xs tracking-[0.2em] text-white/70">{p.n}</span>
                <h3 className="font-display text-3xl md:text-5xl uppercase tracking-mega">{p.t}</h3>
              </div>
              <p className="mt-4 font-body text-base md:text-lg text-white/85 max-w-md">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-24 md:py-40 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6">
          <img src="/images/slow-on-purpose.jpeg" alt="Studio culture" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-5 md:col-start-8 mt-8 md:mt-20">
          <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-4">Studio Culture</p>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-mega leading-[0.95] mb-8"><RevealText>Slow, on purpose.</RevealText></h2>
          <p className="font-body text-lg md:text-xl text-black/70">
            Mornings begin with revisions, because the best ideas usually strike us late at night. Afternoons are spent debating typography, shot selections, and the details most people never notice. Evenings slow down over chai, before tomorrow&apos;s chaos begin all over again.
          </p>
          <p className="mt-6 font-script text-3xl md:text-4xl text-[#F42B29]">— with love, sauce studios.</p>
        </div>
      </section>

      <section className="px-6 md:px-12 py-24 bg-black text-white">
        <h2 className="font-display text-5xl md:text-8xl uppercase tracking-mega leading-[0.9]"><RevealText>Want the long version?</RevealText></h2>
        <div className="mt-10">
          <MagneticButton as={Link} to="/contact" className="inline-flex items-center gap-3 border border-white/30 hover:border-white px-8 py-5" data-testid="about-contact-btn">
            <span className="font-body uppercase text-sm tracking-[0.2em]">Come say hi</span>
            <ArrowUpRight className="w-5 h-5" />
          </MagneticButton>
        </div>
      </section>
    </div>
  );
}
